import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Loader, Send } from "lucide-react";
import { io } from "socket.io-client";
import { BASE_URL } from "../../../configs/WebServiceUrl";
import { lmsGetChatMessages, lmsPostChatMessage } from "../api/lmsApi";

const formatTime = (dateStr) =>
  new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const formatDateLabel = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
};

// Renders + drives one private 1:1 chat thread between a student and a
// staff member (a teacher or the school admin) — completely independent of
// the notice board. Styled as a chat window (scrollable message list +
// pill composer). `viewerType`/`viewerId` is whoever is using this panel
// right now; `studentId`/`staffType`/`staffId` identify the thread itself.
//
// Live updates: joins the thread's socket room — the same room the backend
// broadcasts to from postMessage — so a reply from the other side appears
// without a refresh. The sender's own reply still triggers a normal
// refetch too (belt-and-suspenders if the socket round-trip is slow/
// dropped); incoming socket messages are deduped by id either way.
export default function ChatThreadPanel({
  studentId,
  staffType,
  staffId,
  viewerType,
  viewerId,
  schoolId,
  height = "360px",
  fill = false,
  onMessageSent,
}) {
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  const fetchThread = async () => {
    try {
      const res = await lmsGetChatMessages({
        student_id: studentId,
        staff_type: staffType,
        staff_id: staffId,
      });
      setThread(res.data?.data || []);
    } catch (err) {
      console.error("fetchThread error:", err);
      toast.error("Failed to load conversation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!studentId || !staffType || !staffId) return;

    fetchThread();

    const socket = io(BASE_URL);
    socketRef.current = socket;
    const room = { student_id: studentId, staff_type: staffType, staff_id: staffId };
    const joinRoom = () => socket.emit("joinChatThread", room);

    socket.on("connect", joinRoom);
    joinRoom();

    socket.on("newChatMessage", (msg) => {
      if (
        Number(msg.student_id) !== Number(studentId) ||
        msg.staff_type !== staffType ||
        Number(msg.staff_id) !== Number(staffId)
      ) {
        return;
      }
      setThread((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    });

    return () => {
      socket.emit("leaveChatThread", room);
      socket.off("connect", joinRoom);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, staffType, staffId]);

  // Keep the view pinned to the latest message as the thread grows.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [thread.length, loading]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await lmsPostChatMessage({
        school_id: schoolId,
        student_id: studentId,
        staff_type: staffType,
        staff_id: staffId,
        sender_type: viewerType,
        sender_id: viewerId,
        message: reply,
      });
      setReply("");
      fetchThread();
      onMessageSent?.();
    } catch (err) {
      console.error("postMessage error:", err);
      toast.error(err.response?.data?.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  let lastDateLabel = null;

  return (
    <div
      style={{
        borderTop: fill ? "none" : "1px solid #e2e8f0",
        marginTop: fill ? 0 : "12px",
        paddingTop: fill ? 0 : "12px",
        display: "flex",
        flexDirection: "column",
        width: fill ? "100%" : undefined,
        height: fill ? "100%" : undefined,
        boxSizing: "border-box",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          ...(fill ? { flex: 1, minHeight: 0 } : { height }),
          width: "100%",
          boxSizing: "border-box",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          padding: "10px",
          background: "#f8fafc",
          borderRadius: fill ? "10px 10px 0 0" : "10px",
          marginBottom: fill ? 0 : "10px",
        }}
      >
        {loading ? (
          <div style={{ margin: "auto", fontSize: "13px", color: "#64748b" }}>Loading conversation...</div>
        ) : thread.length === 0 ? (
          <div style={{ margin: "auto", fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>
            No messages yet — start the conversation.
          </div>
        ) : (
          thread.map((m) => {
            const isMine = m.sender_type === viewerType && Number(m.sender_id) === Number(viewerId);
            const dateLabel = formatDateLabel(m.created_at);
            const showDateDivider = dateLabel !== lastDateLabel;
            lastDateLabel = dateLabel;
            return (
              <React.Fragment key={m.id}>
                {showDateDivider && (
                  <div style={{ textAlign: "center", margin: "10px 0 6px" }}>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        background: "#e2e8f0",
                        padding: "2px 10px",
                        borderRadius: "999px",
                      }}
                    >
                      {dateLabel}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    alignSelf: isMine ? "flex-end" : "flex-start",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isMine ? "flex-end" : "flex-start",
                    maxWidth: "75%",
                    margin: "2px 4px",
                  }}
                >
                  <div
                    style={{
                      background: isMine ? "#2563eb" : "#ffffff",
                      color: isMine ? "#fff" : "#0f172a",
                      padding: "8px 12px",
                      borderRadius: isMine ? "14px 14px 2px 14px" : "14px 14px 14px 2px",
                      fontSize: "13px",
                      lineHeight: 1.4,
                      boxShadow: isMine ? "none" : "0 1px 2px rgba(0,0,0,0.06)",
                      border: isMine ? "none" : "1px solid #e2e8f0",
                      wordBreak: "break-word",
                    }}
                  >
                    {m.message}
                  </div>
                  <span style={{ fontSize: "10px", color: "#94a3b8", marginTop: "2px", padding: "0 2px" }}>
                    {formatTime(m.created_at)}
                  </span>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={handleReply}
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
          ...(fill
            ? { padding: "10px 12px", borderTop: "1px solid #e2e8f0", flexShrink: 0 }
            : {}),
        }}
      >
        <input
          type="text"
          placeholder="Write a message..."
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          style={{
            flex: 1,
            border: "1px solid #e2e8f0",
            borderRadius: "999px",
            padding: "10px 16px",
            fontSize: "13px",
            outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={sending || !reply.trim()}
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "50%",
            border: "none",
            background: "#2563eb",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: sending || !reply.trim() ? "not-allowed" : "pointer",
            opacity: sending || !reply.trim() ? 0.6 : 1,
            flexShrink: 0,
          }}
        >
          {sending ? <Loader size={15} className="spinner" /> : <Send size={15} />}
        </button>
      </form>
    </div>
  );
}
